import InflectTablesModel from 'models/InflectTablesModel';
import { get, put } from 'services/request';
import BaseInflectsModel, { IInflectBase } from './BaseInflectsModel';

export type IInflectEnding = IInflectBase & {
  ending: string;
  group: number;
  panelId?: number | null;
};

export default class ConjugationRuleEndingsModel extends BaseInflectsModel<IInflectEnding> {
  withInitials = false;
  ruleId = 0;
  wordClassId = 0;

  setRuleId(ruleId: number) {
    this.ruleId = ruleId;
  }

  protected fillInflects(items: IInflectEnding[]): IInflectEnding[] {
    const allCells = this.getTableCells();

    // Create a map of inflectId to row
    const inflectToRow = new Map<number, number>();
    allCells.forEach((cell) => {
      inflectToRow.set(cell.id, cell.row);
    });

    // Create a map to track which inflect/group combinations exist
    const existingMap = new Map<string, IInflectEnding>();
    items.forEach((item) => {
      const key = `${item.inflectId}-${item.group || 1}`;
      existingMap.set(key, item);
    });

    // Find all groups that exist for each row
    const rowGroups = new Map<number, Set<number>>();

    items.forEach((item) => {
      const row = inflectToRow.get(item.inflectId);
      if (row !== undefined) {
        if (!rowGroups.has(row)) {
          rowGroups.set(row, new Set());
        }
        rowGroups.get(row)!.add(item.group || 1);
      }
    });

    // Ensure every row has at least group 1
    allCells.forEach((cell) => {
      if (!rowGroups.has(cell.row)) {
        rowGroups.set(cell.row, new Set([1]));
      }
    });

    // Build the filled items list
    const filledItems: IInflectEnding[] = [];

    // For each cell, create items for all groups that exist in its row
    allCells.forEach((cell) => {
      const inflectId = cell.id;
      const row = cell.row;
      const groups = rowGroups.get(row) || new Set([1]);

      // Sort groups for consistent ordering
      const sortedGroups = Array.from(groups).sort((a, b) => a - b);

      sortedGroups.forEach((group) => {
        const key = `${inflectId}-${group}`;
        const existingItem = existingMap.get(key);

        if (existingItem) {
          filledItems.push(existingItem);
        } else {
          // Create empty item for missing inflect/group combination
          filledItems.push(this.createEmptyItem(inflectId, 0, group));
        }
      });
    });

    // Re-index all items
    return filledItems.map((item, index) => ({ ...item, index }));
  }

  async loadEndings(ruleId: number, wordClassId: number) {
    if (ruleId <= 0) {
      return;
    }
    await super.loadItemEdit(wordClassId);
    this.loadingItem = true;
    this.render();

    this.ruleId = ruleId;
    this.wordClassId = wordClassId;

    this.inflectTablesModel = new InflectTablesModel({
      courseId: this.courseId,
      wordClass: wordClassId,
    });

    await Promise.all([
      this.inflectTablesModel.getInflectTables(),
      this.inflectTablesModel.getInflectTableHeaders(),
      this.inflectTablesModel.getInflectTableCells(),
    ]);

    const items = await get<IInflectEnding[]>(`conj-rules/${ruleId}/endings`);
    await this.setItem(this.fillInflects(items));

    this.loadingItem = false;
    this.render();
  }

  async save() {
    this.loadingItem = true;
    this.render();

    const filteredAndRenumbered = this.filterAndRenumberGroups(this.itemUpdated);

    await put(`conj-rules/${this.ruleId}/endings`, {
      endings: JSON.stringify(filteredAndRenumbered),
    });

    await this.loadEndings(this.ruleId, this.wordClassId);
  }

  private filterAndRenumberGroups(items: IInflectEnding[]): IInflectEnding[] {
    // Group items by group number
    const groupedItems = new Map<number, IInflectEnding[]>();

    items.forEach((item) => {
      const group = item.group || 1;
      if (!groupedItems.has(group)) {
        groupedItems.set(group, []);
      }
      groupedItems.get(group)!.push(item);
    });

    // Filter out groups that have all empty endings
    const nonEmptyGroups = new Map<number, IInflectEnding[]>();

    groupedItems.forEach((groupItems, groupNum) => {
      const hasNonEmptyEnding = groupItems.some((item) => item.ending !== '');
      if (hasNonEmptyEnding) {
        nonEmptyGroups.set(groupNum, groupItems);
      }
    });

    // Renumber groups sequentially (1, 2, 3...)
    const sortedGroupNums = [...nonEmptyGroups.keys()].sort((a, b) => a - b);
    const renumberedItems: IInflectEnding[] = [];

    sortedGroupNums.forEach((oldGroupNum, index) => {
      const newGroupNum = index + 1;
      const groupItems = nonEmptyGroups.get(oldGroupNum)!;

      groupItems.forEach((item) => {
        // Only include items with non-empty endings
        if (item.ending !== '') {
          renumberedItems.push({
            ...item,
            group: newGroupNum,
          });
        }
      });
    });

    return renumberedItems;
  }

  protected createEmptyItem(inflectId: number, index = 0, group = 1): IInflectEnding {
    return {
      index,
      inflectId,
      ending: '',
      group,
      panelId: null,
    };
  }

  isInflectEmpty(item: IInflectEnding): boolean {
    return item.ending === '';
  }

  addNewGroupForRow(inflectIds: number[]) {
    const rowItems = this.itemUpdated.filter((item: IInflectEnding) => inflectIds.includes(item.inflectId));

    const maxGroup = rowItems.length > 0 ? Math.max(...rowItems.map((item: IInflectEnding) => item.group || 1)) : 0;

    const newGroup = maxGroup + 1;
    const newItems: IInflectEnding[] = [];

    inflectIds.forEach((inflectId) => {
      const newItem = this.createEmptyItem(inflectId, this.itemUpdated.length + newItems.length, newGroup);
      newItems.push(newItem);
    });

    this.itemUpdated = [...this.itemUpdated, ...newItems];
    this.render();
  }
}
